import React from 'react'
import formatDate from 'date-fns/format'
import de from 'date-fns/locale/de'
import es from 'date-fns/locale/es'
import fr from 'date-fns/locale/fr'
import id from 'date-fns/locale/id'
import it from 'date-fns/locale/it'
import ja from 'date-fns/locale/ja'
import ko from 'date-fns/locale/ko'
import pt from 'date-fns/locale/pt-BR'
import th from 'date-fns/locale/th'
import zh from 'date-fns/locale/zh-CN'
import isNumber from 'lodash/isNumber'
import isObject from 'lodash/isObject'

export enum Locales {
  'cn',
  'de',
  'en',
  'es',
  'fr',
  'id',
  'it',
  'ja',
  'ko',
  'pt',
  'th',
}
export type Locale = keyof typeof Locales
export const defaultLocale: Locale = 'en'

let currentLocale: Locale = defaultLocale
let dateTimePattern:
  | NonNullable<Parameters<typeof formatDate>[2]>['locale']
  | undefined = getDateTimePattern(defaultLocale)

export const LocalizationContext = React.createContext<{
  currentLocale: Locale
}>({
  currentLocale: defaultLocale,
})

export function LocalizationProvider(props: { locale: Locale; children: React.ReactNode }) {
  currentLocale = props.locale
  dateTimePattern = getDateTimePattern(props.locale)

  return (
    <LocalizationContext.Provider
      value={{
        currentLocale: props.locale,
      }}
    >
      {props.children}
    </LocalizationContext.Provider>
  )
}

type TranslationInput = string | ({ key: string; en: string } & { [localeCode in Locale]?: string })

type ElementInterpolation = { [key in string]: React.ReactNode }
type StringInterpolation = { [key in string]: string | number }

type TranslationFunction = {
  (input: TranslationInput, interpolation?: ElementInterpolation): React.ReactElement
  string: (input: TranslationInput, interpolation?: StringInterpolation) => string
  date: (date: Date | number) => string
}

function getText(input: TranslationInput) {
  if (typeof input === 'string') {
    return input
  }
  return input[currentLocale] ?? input.en ?? input.key
}

export const t: TranslationFunction = (input, interpolation) => {
  return (
    <LocalizedText
      translationKey={typeof input === 'string' ? input : input.key}
      message={getText(input)}
      variables={interpolation}
    />
  )
}

t.string = (input, interpolation) => {
  const text = getText(input)

  if (interpolation) {
    return Object.keys(interpolation).reduce(
      (name, variable) => name.split(`%{${variable}}`).join(String(interpolation[variable])),
      text,
    )
  }

  return text
}

export const defaultDateFormat = 'MMM d, y'

t.date = (input) => {
  const date = isNumber(input) ? new Date(input * 1000) : input
  const options = { locale: dateTimePattern }

  try {
    return formatDate(date, t.string('MMM d, y'), options)
  } catch (error) {
    return formatDate(date, defaultDateFormat, options)
  }
}

/**
 * @see https://date-fns.org/v2.10.0/docs/I18n
 */
function getDateTimePattern(locale: Locale) {
  switch (locale) {
    case 'cn':
      return zh
    case 'de':
      return de
    case 'es':
      return es
    case 'fr':
      return fr
    case 'id':
      return id
    case 'it':
      return it
    case 'ja':
      return ja
    case 'ko':
      return ko
    case 'pt':
      return pt
    case 'th':
      return th
  }

  // Fallback to en-US which is the default
  return undefined
}

const LOOKUP_KEY = /%\{(.+?)\}/
const SMART_COUNT_DELIMITER = /\s*\|\|\|\|\s*/

function interpolateAsElement(
  message: string,
  variables?: ElementInterpolation,
): Array<React.ReactNode> {
  if (!variables || !isObject(variables)) {
    return [message]
  }

  let intermediateMessage = message

  // Support Polyglot's `%{smart_count}` feature
  const count = variables.smart_count
  if (SMART_COUNT_DELIMITER.test(message) && count !== undefined) {
    const [singular, plural] = message.split(SMART_COUNT_DELIMITER)
    intermediateMessage = count === 1 ? singular : plural
  }

  // Slightly modified from https://github.com/martinandert/react-interpolate-component/blob/0f93b4f6e36ce9b80aa4ff1a43318b4a29a4b363/index.js
  return intermediateMessage.split(LOOKUP_KEY).reduce((memo, match, index) => {
    let child: React.ReactNode
    if (index % 2 === 0) {
      child = match.length > 0 ? match : null // Return null because of more stable React reconciliation
    } else {
      child = variables[match]
    }

    memo.push(child)
    return memo
  }, [] as Array<React.ReactNode>)
}

function LocalizedText(props: {
  translationKey: string
  message: string
  variables?: ElementInterpolation
}): React.ReactElement {
  const introspectionProps = {
    'data-l10n-key': props.translationKey,
  }

  const children = interpolateAsElement(props.message, props.variables)

  // Avoid assigning `key` attribute to each of the children array using `createElement`
  return React.createElement('span', introspectionProps, ...children)
}
